import {
    accountMaskLabel,
    FileNameOf,
    filingNotes,
    heldMessage,
    isFiledOnAnotherAccount,
    wrongAccountMessage,
} from './statementFilings';

import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';
import { UploadedStatementStatus } from 'services/bookkeeping/queries/useStatementUploadStatuses';

/**
 * How a file reads to the member.
 *
 * A presentation vocabulary, not a judgement — every one of these except `unmatched` is the
 * server's `uploaded_statement_statuses` answer under a name the UI already speaks. The
 * frontend used to derive these from the automation job and the tracker rows itself; it no
 * longer does, because the two reads ran on independent clocks and a correctly filed
 * statement could momentarily read as declined.
 *
 * - `processing`    — no answer yet, or the server is still waiting on one.
 * - `error`         — the extraction job failed outright.
 * - `notAStatement` — the job succeeded and produced nothing statement-shaped.
 * - `wrongAccount`  — nothing in the file matches the card it was uploaded under. Says where its
 *                     statements went instead, when they went anywhere.
 * - `noPeriod`      — a statement for this account with no readable month on it.
 * - `notNeeded`     — a valid statement whose month falls outside the required window.
 * - `matched`       — filed, and `coveredPeriods` names the months it bought.
 * - `alreadyHeld`   — read fine, but the month it names on this card is already held, by the
 *                     bank or by another upload. Not a failure: the member has the month.
 * - `unfiled`       — read and declined, and the server gave no holder to name.
 * - `filedElsewhere` — listed under another card and holding no month on this one. Never comes
 *                     from the server, whose answer is about the file's own card.
 * - `unmatched`     — wanted a month and owns none. Never comes from the server: whether a
 *                     file lost its slot is a fact about the *set* of uploads and about
 *                     corrections the member has staged but not saved, so only
 *                     `resolveStatementAssignments` can decide it.
 */
type StatementVerdictKind =
    | 'processing'
    | 'error'
    | 'notAStatement'
    | 'wrongAccount'
    | 'noPeriod'
    | 'notNeeded'
    | 'matched'
    | 'alreadyHeld'
    | 'unfiled'
    | 'filedElsewhere'
    | 'unmatched';

export interface StatementVerdict {
    kind: StatementVerdictKind;
    /**
     * Member-facing copy, where the kind arrives with its own.
     *
     * Empty for `processing` and `matched`, which render no reason, and for `unmatched`, whose
     * wording depends on what the *set* of uploads did and so is derived by the consumer rather
     * than carried here.
     */
    message: string;
    /** Periods this file credited. Non-empty only for `matched`. */
    coveredPeriods: StatementPeriod[];
    /**
     * What the file's other statements did — "Also covers ··4035 (Jul 2026)" — one line each. Kept
     * apart from `message`, which says what it did *here*. Absent when there is nothing to add.
     */
    notes?: string[];
    /**
     * The file already holds a statement on an account other than this card. Hides the month menu
     * on a file that doesn't match this card: the server refuses to file one statement twice.
     */
    isFiledOnAnotherAccount?: boolean;
    /**
     * The mask of the card the file was uploaded under, when that isn't the card whose window this
     * is — "uploaded under ··4034". Absent for a file on its own card.
     */
    uploadedUnderMask?: string;
}

/**
 * A verdict that has been through `resolveStatementAssignments` — what the UI consumes.
 *
 * Distinct from `StatementVerdict` because the two stages genuinely answer different
 * questions, and collapsing them into one optional-field type made every consumer defend
 * against `undefined` for a state that only exists before resolution and never reaches them.
 */
export interface ResolvedStatementVerdict extends StatementVerdict {
    /**
     * The in-window periods the server credited this file with, whatever it ends up owning
     * once the member's staged corrections are applied.
     *
     * Kept separate from `coveredPeriods` because two callers need what was believed
     * *independently* of the outcome: `unmatched` says it out loud ("we believe this is your
     * January statement"), and the assignment flow tests a member's choice against it to
     * decide whether to ask for confirmation. Reading either off `coveredPeriods` would be
     * wrong — that field is emptied precisely when a file stops owning the month.
     */
    believedPeriods: StatementPeriod[];
    /**
     * The tracker rows this document holds, whatever it ends up owning.
     *
     * From the same source the assignments are keyed on, not from what the extraction read — so
     * it is the answer to "which row would a correction here move" and "which months does a
     * sibling row already hold". Empty for a document holding no row.
     */
    heldRows: StatementPeriod[];
    /**
     * The month the member picked for the row this file is addressed by — its first held row,
     * or the document itself when it holds none.
     *
     * Not "the member's choice for this file": a file carrying several statements has one
     * assignment per row, and each row's own answer is on `ownerByPeriodKey`.
     *
     * Carried on the verdict so the resolver stays the single source of "where is this file
     * and who put it there". Reflects the stored choice even when it could not be honoured
     * (an out-of-window month left over from a changed books start date), because otherwise
     * a stale assignment would be impossible to undo — the menu offers that only when it
     * sees a choice to undo.
     */
    assignedPeriod?: StatementPeriod;
}

/**
 * How long a file may sit unanswered before we call it timed out.
 *
 * One constant for one contract, shared by the two halves that have to agree on it: the poll
 * stops asking at this age, and the row stops showing a spinner at the same age. Split into two
 * numbers they would drift, and the gap between them is a file that nothing is watching and
 * nothing admits to having given up on.
 *
 * The server can leave a file on `processing` indefinitely — a document whose account was never
 * recorded is waiting on an async task that may never run — so this is a real state, not a
 * defensive guess.
 */
export const PROCESSING_EXPIRY_MS = 15 * 60 * 1000;

/**
 * A file the server never answered for, old enough that it never will.
 *
 * `processing` is the reading given to a file with no answer yet, which is the only safe one
 * while an answer is still coming — but it is not safe forever. The poll stops at
 * `PROCESSING_EXPIRY_MS`, and past that point a file still reading as in-flight is one nothing
 * is watching: it renders no row of its own, so the member is left with a file they cannot see
 * or delete and no explanation. Ageing it out puts it back in the list with delete restored,
 * which is the move — the file has to go back up.
 *
 * Timed from `uploadedAt` rather than from the poll's clock because this is a fact about the
 * document, and the case it exists for is a member reopening the modal on a batch whose job
 * never ran: there is no poll history to consult, and the row must read as timed out on its
 * first render. Without an `uploadedAt` there is nothing to age against, so it keeps its
 * previous behaviour.
 */
export function hasProcessingExpired(verdict: StatementVerdict | undefined, uploadedAt?: string): boolean {
    if (verdict?.kind !== 'processing' || !uploadedAt) {
        return false;
    }
    const uploadedMs = new Date(uploadedAt).getTime();
    return Number.isFinite(uploadedMs) && Date.now() - uploadedMs > PROCESSING_EXPIRY_MS;
}

/**
 * Still genuinely in flight — an answer is expected.
 *
 * Distinct from `kind === 'processing'`, which stays true forever for a file the server never
 * answered for. Every count of "waiting" has to use this instead: a spinner or a disabled Save
 * driven off the raw kind would never come back for a document whose job never ran, and the
 * row for that document is meanwhile telling the member to delete it.
 */
export function isAwaitingVerdict(verdict: StatementVerdict | undefined, uploadedAt?: string): boolean {
    return verdict?.kind === 'processing' && !hasProcessingExpired(verdict, uploadedAt);
}

export const VERDICT_MESSAGES = {
    /**
     * Both point at the month menu first. Choosing a month files the statement whether or not we
     * could read it, which is the way forward when re-uploading the same file would fail again.
     */
    error: "We couldn't read this file. Choose its month to file it, or upload it again.",
    notAStatement:
        "We couldn't read this as a bank statement. If it is one, choose its month to file it. Otherwise, upload the correct statement.",
    wrongAccount: "This statement doesn't match this account. Please upload the correct statement.",
    /**
     * The file is a statement for the right account, but no statement period could be read off
     * it. Separate from `notNeeded`, which knows the month and doesn't want it: here we cannot
     * tell which month this is, so the member has to say. Pointing at the month menu is the
     * action, since re-uploading the same unreadable file changes nothing.
     */
    noPeriod: "We couldn't tell which month this statement covers. Pick its month to file it.",
    /**
     * A valid statement for this account whose month falls outside the required window.
     *
     * Nothing is wrong with the file and there is nothing for the member to do, but it has to
     * say so out loud: this used to render as a silent stacked row, and in a layout whose only
     * rows are period slots and the couldn't-match list, silence means no row at all. A file
     * the member uploaded that appears nowhere reads as a lost upload, so the copy explains
     * the absence of credit rather than implying a failure.
     */
    notNeeded: "This statement covers a month we don't need, so it isn't counted.",
    /**
     * The fallback when the server names no holder. A known holder reads `alreadyHeld` instead,
     * since "try re-uploading" is the wrong advice for a month that is already covered.
     */
    unfiled: "We read this statement but couldn't file it. Please try re-uploading, or contact support.",
    /**
     * Nothing ever came back for this file. Distinct from `error`, where a job ran and failed:
     * here no job is known to have run at all, so "try again" is the whole of the advice and
     * deleting the row is how the member gets back to a state they can act on.
     */
    expired: 'This file is taking longer than expected. Please delete it and try uploading again.',
} as const;

/** A verdict with its notes, set only when there are some, so "no notes" keeps the old shape. */
function withNotes(verdict: StatementVerdict, notes: string[]): StatementVerdict {
    return notes.length > 0 ? { ...verdict, notes } : verdict;
}

/**
 * How a file reads in a card's window when its served status is about another card: judged by its
 * coverage rows here, since its `periods` are its home card's. `homeMask` is set only when the home
 * is a different card. Holding nothing here, it says where it is rather than vanishing.
 */
export function verdictAwayFromHome(rowsHere: StatementPeriod[], homeMask: string | undefined): StatementVerdict {
    if (rowsHere.length > 0) {
        return {
            kind: 'matched',
            message: '',
            coveredPeriods: rowsHere,
            ...(homeMask && { uploadedUnderMask: homeMask }),
        };
    }
    return {
        kind: 'filedElsewhere',
        message: `This statement is listed under ${accountMaskLabel(homeMask)}, so it isn't counted here.`,
        coveredPeriods: [],
    };
}

/**
 * The server's answer for one file, in the UI's vocabulary.
 *
 * A rename plus the copy for where the file's statements went. The ordering that used to live here
 * (extraction-derived reasons before the tracker row, so an out-of-window statement with a real
 * row still reads `notNeeded`) is now the server's, decided without reading a tracker at all.
 *
 * Accounts are relative to the file's home card. `fileNameOf` names a holding file; without it the
 * holder reads "another file".
 *
 * An absent status is `processing`: the file has been uploaded and the server has not spoken
 * about it yet. That is the same reading the old ladder gave a document with no job, and it
 * is the only safe one — the alternative is telling a member their statement was refused
 * before anything has looked at it.
 */
export default function toStatementVerdict(
    status: UploadedStatementStatus | undefined,
    fileNameOf: FileNameOf = () => undefined
): StatementVerdict {
    const filings = status?.statement_filings ?? [];
    const home = status?.bank_account_id ?? null;

    switch (status?.status) {
        case 'tracked':
            // A home-card statement held while another filed (June filed, July taken) is a note
            // here: the file's headline is the month it did fill.
            return withNotes(
                { kind: 'matched', message: '', coveredPeriods: status.periods },
                filingNotes(filings, home, fileNameOf, { includeHomeHeld: true })
            );
        case 'failed':
            return { kind: 'error', message: VERDICT_MESSAGES.error, coveredPeriods: [] };
        case 'not_a_statement':
            return { kind: 'notAStatement', message: VERDICT_MESSAGES.notAStatement, coveredPeriods: [] };
        case 'wrong_account':
            // Where its statements went is folded into the message rather than added as notes:
            // for this file it is the whole answer, not a footnote to one.
            return {
                kind: 'wrongAccount',
                message: wrongAccountMessage(filings, home, fileNameOf) ?? VERDICT_MESSAGES.wrongAccount,
                coveredPeriods: [],
                ...(isFiledOnAnotherAccount(filings, home) && { isFiledOnAnotherAccount: true }),
            };
        case 'no_period':
            return { kind: 'noPeriod', message: VERDICT_MESSAGES.noPeriod, coveredPeriods: [] };
        case 'not_needed':
            return { kind: 'notNeeded', message: VERDICT_MESSAGES.notNeeded, coveredPeriods: [] };
        case 'unfiled': {
            const otherAccountNotes = filingNotes(filings, home, fileNameOf, { includeHomeHeld: false });
            const held = heldMessage(filings, home, fileNameOf);
            return withNotes(
                held
                    ? { kind: 'alreadyHeld', message: held, coveredPeriods: [] }
                    : { kind: 'unfiled', message: VERDICT_MESSAGES.unfiled, coveredPeriods: [] },
                otherAccountNotes
            );
        }
        // Deliberately a runtime fallback and not an exhaustiveness check: this is the one
        // boundary where an unknown value is expected rather than a bug. A server ahead of this
        // build can send a status the union has never heard of, and reading it as "no answer
        // yet" is the only safe guess. Widening `UploadedStatementStatus` is what surfaces the
        // new kind — `routeStatementRow` fails to compile until someone decides how it reads.
        case 'processing':
        default:
            return { kind: 'processing', message: '', coveredPeriods: [] };
    }
}
